<template>
  <div>
    <!-- Sidebar para aprovar/reprovar -->
    <consultant-certificate-sidebar/>
    <new-certificate-type-sidebar />
    <manage-certificate-types-sidebar />
    <!-- Breadcrumb + botão -->
    <div style="position: relative">
      <app-breadcrumb :breadcrumbTitle="false">
        <b-dropdown
          variant="primary"
          text="Gestão de cursos"
          right
          style="position: absolute; right: 13px"
        >
          <b-dropdown-item @click="openNewCertificateType">
            <feather-icon icon="PlusCircleIcon" class="mr-50" />
            Novo curso
          </b-dropdown-item>

          <b-dropdown-item @click="openManageCertificateTypes">
            <feather-icon icon="SettingsIcon" class="mr-50" />
            Editar / Desativar cursos
          </b-dropdown-item>
        </b-dropdown>
      </app-breadcrumb>
    </div>

    <!-- Filtros -->
    <b-card no-body>
      <b-card-header class="pb-50">
        <h5 class="mb-1">Filtros</h5>
      </b-card-header>

      <b-card-body>
        <b-row>
          <!-- Nome do PN -->
          <b-col cols="12" md="4" class="mb-1">
            <b-form-group label="Nome do PN" class="mt-1">
              <b-form-input
                v-model="filters.consultant"
                @keyup.enter="search"
              />
            </b-form-group>
          </b-col>

          <!-- Tipo de Certificado (múltiplo) -->
          <b-col cols="12" md="4" class="mb-1">
            <b-form-group label="Tipo do certificado" class="mt-1">
              <v-select
                id="certificate-type"
                multiple
                v-model="filters.types"
                :reduce="t => t.id"
                :options="certificateTypeOptions"
                label="name"
                :disabled="loadingFilters.types"
              />
            </b-form-group>
          </b-col>

          <!-- Status (múltiplo) -->
          <b-col cols="12" md="4">
            <b-form-group label="Status" class="mt-1">
              <v-select
                multiple
                v-model="filters.status"
                :reduce="s => s.key"
                :options="statusOptions"
                label="label"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Botão pesquisar -->
        <b-row>
          <b-col cols="12" class="text-right">
            <b-button variant="outline-primary" @click.prevent="search()">
              <b-spinner v-if="loading" small />
              <feather-icon v-else icon="SearchIcon" class="mr-50" />
              <span>{{ loading ? "Aguarde..." : "Pesquisar" }}</span>
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Tabela -->
    <b-card no-body>
      <div class="m-2">
        <b-row>
          <!-- Items per page -->
          <b-col cols="12" md="4" class="d-flex align-items-center">
            <label class="mb-0">Exibir</label>
            <v-select
              v-model="itemsPerPage"
              :options="itemsPerPageOptions"
              :clearable="false"
              class="per-page-selector mx-50"
              @input="search(currentPage)"
            />
            <label class="mb-0">itens</label>
          </b-col>
        </b-row>
      </div>

      <!-- TABLE -->
      <b-table
        hover
        class="position-relative text-center"
        :items="items"
        :fields="tableColumns"
        responsive
        show-empty
        :busy="loading"
      >
        <!-- Col: ações -->
        <template #cell(actions)="data">
          <b-button-group>

            <b-button
              :id="`action-${data.item.id}`"
              v-if="data.item.status === 'pending'"
              class="btn-icon mr-1"
              variant="flat"
              style="padding: 0"
              @click="openSidebarForApproval(data.item)"
            >
              <feather-icon icon="CheckSquareIcon" class="text-body" />
            </b-button>

            <b-tooltip 
              v-if="data.item.status === 'pending'"
              :target="`action-${data.item.id}`">
              Avaliar
            </b-tooltip>

          </b-button-group>
        </template>
      </b-table>

      <!-- Paginação -->
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-start"
          >
            <span class="text-muted"
              >Exibindo {{ pageItemsFrom }} a {{ pageItemsTo }} de
              {{ totalItems }} itens</span
            >
          </b-col>

          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalItems"
              :per-page="itemsPerPage"
              first-number
              last-number
              prev-class="prev-item"
              next-class="next-item"
              @change="search"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BCardHeader,
  BCardBody,
  BTable,
  BPagination,
  BSpinner,
  BButtonGroup,
  BTooltip,
} from "bootstrap-vue";

import vSelect from "vue-select";
import AppBreadcrumb from "@core/layouts/components/AppBreadcrumb.vue";

import ConsultantCertificateSidebar from "@/modules/consultant/components/ConsultantCertificateSidebar.vue";
import NewCertificateTypeSidebar from "@/modules/certificate/components/NewCertificateTypeSidebar.vue";
import ManageCertificateTypesSidebar from "@/modules/certificate/components/ManageCertificateTypesSidebar.vue";

import { mapActions, mapGetters, mapMutations } from "vuex";
import * as types from "../store/types";
import * as sharedTypes from "@/modules/shared/store/types";
import * as consultantTypes from "@/modules/consultant/store/types";

import { certificateStatusOptions } from "@/constants/certificates_status";

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BPagination,
    BSpinner,
    BButtonGroup,
    BTooltip,
    vSelect,
    AppBreadcrumb,
    ConsultantCertificateSidebar,
    NewCertificateTypeSidebar,
    ManageCertificateTypesSidebar,
  },

  data() {
    return {
      loading: false,
      loadingFilters: {
        types: false,
      },

      // filtros
      filters: {
        consultant: undefined,
        status: undefined,
        types: undefined,
      },

      currentPage: 1,
      itemsPerPage: 10,

      tableColumns: [
        { key: "consultant_name", label: "PN" },
        { key: "certificate_type_name", label: "Certificado" },
        { key: "status_label", label: "Status" },
        { key: "approved_by_name", label: "Aprovado por" },
        { key: "approved_at_formatted", label: "Aprovado em" },
        { key: "actions", label: "Ações" },
      ],
    };
  },

  computed: {
  ...mapGetters({
        certificateTypes: types.CERTIFICATE_TYPES,
        searchResult: types.CERTIFICATES_SEARCH_RESULT,
        itemsPerPageOptions: sharedTypes.ITEMS_PER_PAGE_OPTIONS,
        initialItemsPerPage: sharedTypes.INITIAL_ITEMS_PER_PAGE,
    }),

    certificateTypeOptions() {
      return this.certificateTypes || [];
    },

    statusOptions() {
    return certificateStatusOptions;
    },
    
    items() {
      return this.searchResult?.data || [];
    },
    totalItems() {
      return this.searchResult?.total || 0;
    },
    pageItemsFrom() {
      return this.searchResult?.from || 0;
    },
    pageItemsTo() {
      return this.searchResult?.to || 0;
    },
  },

  async mounted() {
    this.itemsPerPage = this.initialItemsPerPage;
    await this.search();

    // carregar tipos
    this.loadingFilters.types = true;
    this.getCertificateTypes().finally(() => {
      this.loadingFilters.types = false;
    });
  },

  methods: {
  ...mapActions({
    searchCertificates: types.SEARCH_CERTIFICATES,
    getCertificateTypes: types.GET_CERTIFICATE_TYPES,
    openNewCertificateTypeSidebar: types.OPEN_NEW_CERTIFICATE_TYPE_SIDEBAR,
    openManageCertificateTypes: types.OPEN_MANAGE_CERTIFICATE_TYPES_SIDEBAR,
  }),

  ...mapMutations({
    mutateConsultantCertificateSidebar: consultantTypes.MUTATE_CONSULTANT_CERTIFICATE_SIDEBAR,
  }),

  search(currentPage) {
    this.loading = true;
    this.currentPage = Number.isInteger(currentPage) ? currentPage : 1;

    const payload = {
      consultant_name: this.filters.consultant || undefined,

      status: this.filters.status && this.filters.status.length
        ? this.filters.status
        : undefined,

      certificate_type_id: this.filters.types && this.filters.types.length
        ? this.filters.types
        : undefined,

      currentPage: this.currentPage,
      limit: this.itemsPerPage,
    };

    this.searchCertificates(payload)
      .finally(() => {
        this.loading = false;
      });
  },

    openNewCertificateType() {
      this.openNewCertificateTypeSidebar({
        saveAction: () => this.getCertificateTypes()
      });
    },
    
    openSidebarForApproval(item) {
    this.mutateConsultantCertificateSidebar({
      visible: true,
      consultant_id: item.consultant_id,
      id: item.id,
      saveAction: () => {
          this.search(this.currentPage)
        }
    });
    },
  },
};
</script>

<style scoped>
.per-page-selector {
  width: 90px;
}
</style>
